import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ButtonColor, ButtonGroup, Icon, TextButton } from '../../../components';
import { ACTIVE_TAB_PARAM, AppRoutes, CONFETTI_PARAM } from '../../../constants';
import { useAppDispatch, useAppSelector, useCheckRequiredProspectFields, useHandleApiResponse } from '../../../hooks';
import {
  randomizeAccount,
  randomizeLead,
  randomizePersonal,
  randomizeScenario,
  setMode,
  setPersonaPrompt,
} from '../../../redux/reducers';
import { useCreatePracticeProspectMutation } from '../../../services';
import {
  LLMProviderID,
  Permissions,
  ProspectLastTouchpoint,
  ProspectPageMode,
  ProspectPageTabs,
  ProspectType,
  SimulationsTab,
  UpdatedPracticeProspectData,
} from '../../../types';
import { conditionalObject } from '../../../utils';

const CREATE_PROSPECT_ERROR_MSG = 'Failed to create prospect';

interface CreateEndActionsProps {
  activeTab: ProspectPageTabs;
  isSuperAdmin: boolean;
  visitedTabs: Set<ProspectPageTabs>;
  setActiveTab: Dispatch<SetStateAction<ProspectPageTabs>>;
  setVisitedTabs: Dispatch<SetStateAction<Set<ProspectPageTabs>>>;
}

const CreateEndActions = ({
  activeTab,
  isSuperAdmin,
  visitedTabs,
  setActiveTab,
  setVisitedTabs,
}: CreateEndActionsProps) => {
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const { fields, notes, orgId, type, category, tags } = useAppSelector((state) => state.prospect);

  const user = useAppSelector((state) => state.auth.user);
  const canManageSims = (user?.permissions || []).includes(Permissions.MANAGE_PROSPECT);

  const [createPracticeProspect, { isLoading: isCreatingProspect }] = useCreatePracticeProspectMutation();

  const areRequiredFieldsFilled = useCheckRequiredProspectFields();
  const handleApiResponse = useHandleApiResponse();

  const activeTabIndex = Object.values(ProspectPageTabs).indexOf(activeTab);
  const nextTab = Object.values(ProspectPageTabs).find((_, index) => index === activeTabIndex + 1);
  // The next tab is disabled if it has not been visited before and all required fields in preceding tabs have not been filled out.
  const isNextTabDisabled = !!nextTab && !visitedTabs.has(nextTab) && !areRequiredFieldsFilled(activeTab);

  const handleNextSection = () => {
    if (!nextTab) return;
    setActiveTab(nextTab);
    setVisitedTabs((prev) => new Set(prev).add(nextTab));
  };

  const onCreateSuccess = useCallback(
    (newData: UpdatedPracticeProspectData) => {
      const {
        personaPrompt: newPersonaPrompt,
        isDraft: newIsDraft,
        isPromptEdited: newIsPromptEdited,
      } = newData.prospect;
      dispatch(setPersonaPrompt({ value: newPersonaPrompt, isUserModified: newIsPromptEdited }));

      // For super admins creating non-draft non-custom prospects, navigate to prompt editing.
      if (isSuperAdmin && !newIsDraft && type !== ProspectType.CUSTOM) {
        setActiveTab(ProspectPageTabs.PROMPT);
        dispatch(setMode(ProspectPageMode.EDIT));
        navigate(`${AppRoutes.PROSPECT}/${newData.prospect.personaId}`, { replace: true });
      } else {
        // For all other cases, navigate to simulations page with appropriate tab.
        const navigateToTab = newIsDraft ? SimulationsTab.DRAFTS : SimulationsTab.LIBRARY;
        const searchParams = new URLSearchParams();
        searchParams.set(ACTIVE_TAB_PARAM, navigateToTab);
        searchParams.set(CONFETTI_PARAM, 'true');
        navigate({
          pathname: AppRoutes.SIMULATIONS,
          search: searchParams.toString(),
        });
      }
    },
    [isSuperAdmin, type, dispatch, navigate]
  );

  const handleCreateProspect = useCallback(async () => {
    if (
      !type ||
      !category ||
      !fields.firstName.value ||
      !fields.lastName.value ||
      !fields.personaVoiceId.value ||
      !fields.jobTitle.value ||
      !fields.accountName.value ||
      !fields.industry.value ||
      !fields.firstMessage.value
    )
      return;

    const customFields = {
      personaPrompt: fields.personaPrompt,
    };

    const nonCustomFields = {
      priorities: fields.priorities.map((priority) => priority.value),
      isDynamicPriorities: fields.isDynamicPriorities,
      // Only include numOfDynamicPriorities if isDynamicPriorities is true.
      ...conditionalObject(fields.isDynamicPriorities, {
        numOfDynamicPriorities: fields.numOfDynamicPriorities,
      }),
      objections: fields.objections.map((objection) => objection.value),
      isDynamicObjections: fields.isDynamicObjections,
      // Only include numOfDynamicObjections if isDynamicObjections is true.
      ...conditionalObject(fields.isDynamicObjections, {
        numOfDynamicObjections: fields.numOfDynamicObjections,
      }),
      questions: fields.questions.map((question) => ({
        question: question.value,
        answer: question.answer,
        points: question.points,
      })),
      isAnsweredQuestions: fields.isAnsweredQuestions,
      isDynamicQuestions: fields.isDynamicQuestions,
      // Only include numOfDynamicQuestions if isDynamicQuestions is true.
      ...conditionalObject(fields.isDynamicQuestions, {
        numOfDynamicQuestions: fields.numOfDynamicQuestions,
      }),
      // NONE is not a valid value for lastTouchpoint, so we don't include it in the request.
      ...conditionalObject(fields.lastTouchpoint.value !== ProspectLastTouchpoint.NONE, {
        lastTouchpoint: fields.lastTouchpoint.value,
      }),
      successResult: fields.successResult.value,
      hiddenContexts: fields.hiddenContexts.map((context) => context.value),
      successDifficultyLevel: fields.successDifficultyLevel.value,
    };

    try {
      const response = await createPracticeProspect({
        orgId,
        type,
        category,
        notes,
        tags: tags.map((tag) => tag.id),

        // Personal
        firstName: fields.firstName.value,
        lastName: fields.lastName.value,
        age: fields.age.value,
        pronouns: fields.pronouns.value,
        talkativenessLevel: fields.talkativenessLevel.value,
        figurativeThinkingLevel: fields.figurativeThinkingLevel.value,
        empathyLevel: fields.empathyLevel.value,
        improvisationLevel: fields.improvisationLevel.value,
        personaVoiceId: fields.personaVoiceId.value,
        firstMessage: fields.firstMessage.value,
        language: fields.language,

        // Lead
        jobTitle: fields.jobTitle.value,
        tenure: fields.tenure.value,
        roleType: fields.roleType.value,
        department: fields.department.value,
        numOfDirectReports: fields.numOfDirectReports.value,

        // Account
        accountName: fields.accountName.value,
        industry: fields.industry.value,
        hqLocation: fields.hqLocation.value,
        remoteType: fields.remoteType.value,
        isMultiSite: fields.isMultiSite.value,
        employeesFrom: fields.employeesFrom.value,
        // Only include employeesTo if employeesFrom is filled out.
        ...conditionalObject(fields.employeesFrom.value !== undefined, {
          employeesTo: fields.employeesTo,
        }),
        annualRevenueFrom: fields.annualRevenueFrom.value,
        // Only include annualRevenueTo if annualRevenueFrom is filled out.
        ...conditionalObject(fields.annualRevenueFrom.value !== undefined, {
          annualRevenueTo: fields.annualRevenueTo,
        }),
        fundingRaisedType: fields.fundingRaisedType.value,

        // Scenario
        ...(type === ProspectType.CUSTOM ? customFields : nonCustomFields),

        // Configuration
        isHidden: fields.isHidden,
        screenStyle: fields.screenStyle,

        // Only pass manager settings if the user has manage prospect permissions.
        ...conditionalObject(canManageSims, {
          scorecardTemplateId: fields.scorecardTemplateId,
          coachingTemplateId: fields.coachingTemplateId,
          cooldownPeriod: fields.cooldownPeriod,
          timeLimit: fields.timeLimit,
          managerNotes: fields.managerNotes,
          folderId: fields.folderId,
          allowedCallers: fields.allowedCallers.map((caller) => caller.id),
          allowedTeams: fields.allowedTeams.map((team) => team.id),
          isSubscribed: fields.isSubscribed,
        }),

        // Only pass super admin settings if the user has super admin permissions.
        ...conditionalObject(isSuperAdmin, {
          isRestricted: fields.isRestricted,
          breathingRoom: fields.breathingRoom,
          numOfInitialShortResponses: fields.numOfInitialShortResponses,
          objectionRate: fields.objectionRate,
          questionRate: fields.questionRate,
          numOfRequiredUncoveredPriorities: fields.numOfRequiredUncoveredPriorities,
          isHangupFeatureEnabled: fields.isHangupFeatureEnabled,
          ...conditionalObject(fields.isHangupFeatureEnabled, {
            // Only include hangupPrompt if the hangup feature is enabled.
            hangupPrompt: fields.hangupPrompt,
          }),
          buyNewPhoneNumber: fields.buyNewPhoneNumber,
          ttsModelId: fields.ttsModelId,
          sttModel: fields.sttModel,
          sttModelId: fields.sttModelId,
          llmModel: fields.llmModel,
          llmModelId: fields.llmModelId,
          llmTemperature: fields.llmTemperature,
          llmMaxTokens: fields.llmMaxTokens,
          ...conditionalObject(fields.llmModel === LLMProviderID.GPT, {
            llmFrequencyPenalty: fields.llmFrequencyPenalty,
            llmPresencePenalty: fields.llmPresencePenalty,
          }),
          promptTemplateId: fields.promptTemplateId,
        }),
      });
      handleApiResponse({
        response,
        errorMsg: CREATE_PROSPECT_ERROR_MSG,
        onSuccess: onCreateSuccess,
      });
    } catch (error) {
      console.error(CREATE_PROSPECT_ERROR_MSG, error);
    }
  }, [
    canManageSims,
    fields,
    isSuperAdmin,
    notes,
    orgId,
    type,
    category,
    tags,
    createPracticeProspect,
    handleApiResponse,
    onCreateSuccess,
  ]);

  // Fills the prospect form section with random values.
  const fillForMeHandler = useMemo(() => {
    switch (activeTab) {
      case ProspectPageTabs.PERSONAL:
        return () => {
          dispatch(randomizePersonal());
        };
      case ProspectPageTabs.LEAD:
        return () => {
          dispatch(randomizeLead());
        };
      case ProspectPageTabs.ACCOUNT:
        return () => {
          dispatch(randomizeAccount());
        };
      case ProspectPageTabs.SCENARIO: {
        const handler =
          type === ProspectType.CUSTOM // Custom prospects have no randomization in the scenario tab.
            ? undefined
            : () => {
                dispatch(randomizeScenario());
              };
        return handler;
      }
      default:
        return undefined;
    }
  }, [activeTab, dispatch]);

  // Show the Config tab on creation only for super admins.
  const canCreate = isSuperAdmin
    ? activeTab === ProspectPageTabs.CONFIGURATION
    : activeTab === ProspectPageTabs.SCENARIO;

  return (
    <ButtonGroup loading={isCreatingProspect}>
      <TextButton
        text={canCreate ? 'Create' : 'Next section'}
        onClick={canCreate ? handleCreateProspect : handleNextSection}
        disabled={isNextTabDisabled}
        tooltip={isNextTabDisabled ? 'Please make sure all required fields are filled.' : undefined}
      />
      <TextButton startIcon={Icon.WAND} color={ButtonColor.TERTIARY} text="Fill for me" onClick={fillForMeHandler} />
    </ButtonGroup>
  );
};

export default CreateEndActions;
